import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Eraser, PenLine } from 'lucide-react';

// Canvas de assinatura (desenho via mouse/touch). Emite o PNG (dataURL) em onChange.
export default function SignaturePad({ value, onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasDrawing, setHasDrawing] = useState(!!value);

  // Prepara o canvas (resolução x DPR) — feito uma vez.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    setupCanvas();
  }, [setupCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (!hasDrawing) setHasDrawing(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange && canvasRef.current) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    setupCanvas();
    setHasDrawing(false);
    if (onChange) onChange('');
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-lg border-2 border-dashed border-gray-700 bg-white overflow-hidden" style={{ height: 150 }}>
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full h-full touch-none cursor-crosshair"
          style={{ width: '100%', height: '100%' }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {!hasDrawing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="flex items-center gap-2 text-gray-400 text-sm">
              <PenLine size={16} /> Assine aqui (toque/mouse)
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        disabled={disabled}
        data-testid="signature-clear"
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-white disabled:opacity-40"
      >
        <Eraser size={14} /> Limpar assinatura
      </button>
    </div>
  );
}
